/**
 * @file api-delivery-monitoring.ts
 * @description API-клиент контроля доставки (phase19.13 шаги 5-6).
 *
 * Зеркалит `apps/api/src/orders/shipments/delivery-monitoring.controller.ts`:
 *  - GET   /admin/shipments/attention   — «Требуют внимания» + счётчики бейджа;
 *  - GET   /admin/shipments/:id/events  — лента событий отгрузки;
 *  - GET   /admin/delivery-settings     — пороги мониторинга;
 *  - PATCH /admin/delivery-settings     — их правка.
 *
 * Подписи этапов и источников — реестрами (`DELIVERY_STAGE_LABEL`,
 * `SHIPMENT_EVENT_SOURCE_LABEL`): новый этап у перевозчика = новая запись,
 * а не ветка в разметке.
 */

import { apiFetch } from "./api-client";

// ─── Лента событий доставки ─────────────────────────────────────────────────

/** Детальный этап доставки (enum `DeliveryStage` на сервере). */
export type DeliveryStage =
  | "CREATED"
  | "ACCEPTED"
  | "IN_TRANSIT"
  | "ARRIVED_DESTINATION"
  | "READY_FOR_PICKUP"
  | "OUT_FOR_DELIVERY"
  | "DELIVERED"
  | "NOT_DELIVERED"
  | "RETURNED";

/** Менеджерские подписи этапов: читает человек, а не разработчик. */
export const DELIVERY_STAGE_LABEL: Record<DeliveryStage, string> = {
  CREATED: "Заявка создана",
  ACCEPTED: "Принят перевозчиком",
  IN_TRANSIT: "В пути",
  ARRIVED_DESTINATION: "Прибыл в город назначения",
  READY_FOR_PICKUP: "Готов к выдаче в ПВЗ",
  OUT_FOR_DELIVERY: "Передан курьеру",
  DELIVERED: "Вручён",
  NOT_DELIVERED: "Вручение не удалось",
  RETURNED: "Возвращён отправителю",
};

/** Откуда получено событие. */
export type ShipmentEventSource = "WEBHOOK" | "SYNC" | "MANUAL";

/** Подписи источников для бейджа события. */
export const SHIPMENT_EVENT_SOURCE_LABEL: Record<ShipmentEventSource, string> = {
  WEBHOOK: "вебхук",
  SYNC: "синхронизация",
  MANUAL: "вручную",
};

/** Одно событие ленты доставки. */
export interface ShipmentEventDto {
  id: string;
  /** Исходный код статуса перевозчика («ACCEPTED_AT_PICK_UP_POINT»). */
  carrierCode: string;
  stage: DeliveryStage;
  occurredAt: string;
  city: string | null;
  comment: string | null;
  source: ShipmentEventSource;
  /**
   * Сырой payload перевозчика.
   *
   * Сервер отдаёт его только тем, кто разбирает инциденты (право на управление
   * перевозчиками). Поле опционально намеренно: кнопка `[{ }]` и Sheet с JSON
   * рисуются, только когда `raw` реально пришёл, — менеджеру без права поле
   * просто не приезжает.
   */
  raw?: unknown;
  /**
   * Кто поставил ручную отметку (для MANUAL — «вручную · {имя}»).
   * null — событие от перевозчика либо автор не сохранился.
   */
  actorName: string | null;
}

/** Страница ленты событий. */
export interface ShipmentEventsPageDto {
  items: ShipmentEventDto[];
  total: number;
  limit: number;
  offset: number;
}

/** Лента событий отгрузки, новые сверху. */
export async function listShipmentEvents(
  shipmentId: string,
  params: { limit?: number; offset?: number } = {},
): Promise<ShipmentEventsPageDto> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  return apiFetch<ShipmentEventsPageDto>(
    `/admin/shipments/${shipmentId}/events${qs ? `?${qs}` : ""}`,
  );
}

// ─── «Требуют внимания» ─────────────────────────────────────────────────────

/** Почему отправление попало в список (реестр правил сервера). */
export type ShipmentAttentionRule =
  | "track_not_assigned"
  | "no_movement"
  | "pickup_wait"
  | "not_delivered";

/** Строка списка «Требуют внимания». */
export interface ShipmentAttentionItemDto {
  shipmentId: string;
  orderId: string;
  orderNumber: string;
  carrier: string;
  trackNumber: string | null;
  status: string;
  stage: DeliveryStage | null;
  rule: ShipmentAttentionRule;
  sinceAt: string;
  hoursElapsed: number;
  daysElapsed: number;
}

/**
 * Почему в очередь попал ЗАКАЗ, а не отгрузка (phase19.15).
 * Пока правило одно — заявка «по договорённости» без расчёта дольше SLA.
 */
export type OrderAttentionRule = "delivery_agreement_sla";

/** Строка очереди по заказу: у неё нет ни отгрузки, ни трека. */
export interface OrderAttentionItemDto {
  orderId: string;
  orderNumber: string;
  rule: OrderAttentionRule;
  /** С какого момента ждём действия менеджера (ISO). */
  sinceAt: string;
  hoursElapsed: number;
  /** Готовая подпись строки — словарь общий с сервером. */
  title: string;
}

/** Ответ эндпоинта: строки + счётчики для бейджа меню. */
export interface ShipmentAttentionListDto {
  items: ShipmentAttentionItemDto[];
  /**
   * phase19.15 — заявки на согласование доставки. Отдельный массив, а не
   * подмешивание в `items`: половина полей строки отгрузки у них пустая.
   */
  orders?: OrderAttentionItemDto[];
  counts: {
    /** Всего строк очереди — отгрузки И заказы. */
    total: number;
    byRule: Record<ShipmentAttentionRule, number>;
    /** Счётчики блока заказов; `undefined` — сервер ещё не отдаёт. */
    ordersByRule?: Record<OrderAttentionRule, number>;
  };
}

/** Короткая подпись правила по заказу — для чипа-счётчика. */
export const ORDER_ATTENTION_RULE_SHORT: Record<OrderAttentionRule, string> = {
  delivery_agreement_sla: "Согласование доставки",
};

/** Отправления магазина, требующие вмешательства (сортировка серверная). */
export async function listShipmentsAttention(): Promise<ShipmentAttentionListDto> {
  return apiFetch<ShipmentAttentionListDto>("/admin/shipments/attention");
}

/** Тон строки: «не вручено» и «риск возврата» — красные, остальное — янтарь. */
export const SHIPMENT_ATTENTION_RULE_TONE: Record<
  ShipmentAttentionRule,
  "warning" | "destructive"
> = {
  not_delivered: "destructive",
  pickup_wait: "destructive",
  track_not_assigned: "warning",
  no_movement: "warning",
};

/** Часы/дни → человеческая длительность («6 ч», «4 дн»). */
export function formatElapsed(hoursElapsed: number): string {
  if (hoursElapsed < 24) return `${Math.max(1, Math.round(hoursElapsed))} ч`;
  return `${Math.max(1, Math.round(hoursElapsed / 24))} дн`;
}

/**
 * Правило по-русски вместе с длительностью — то, что менеджер читает первым.
 * Реестром здесь быть нечему: у каждого правила своя формулировка с числом.
 */
export function attentionRuleText(rule: ShipmentAttentionRule, hoursElapsed: number): string {
  const elapsed = formatElapsed(hoursElapsed);
  switch (rule) {
    case "not_delivered":
      return "Вручение не удалось";
    case "pickup_wait":
      return `В ПВЗ ${elapsed} — риск возврата`;
    case "track_not_assigned":
      return `Трек не присвоен ${elapsed}`;
    case "no_movement":
      return `Без движения ${elapsed}`;
  }
}

// ─── Пороги контроля отправлений ────────────────────────────────────────────

/** Пороги мониторинга магазина (дефолты сервера 4 ч / 3 дн / 5 дн). */
export interface DeliverySettingsDto {
  staleTrackTimeoutHours: number;
  staleNoMovementDays: number;
  stalePickupWaitDays: number;
}

export async function getDeliverySettings(): Promise<DeliverySettingsDto> {
  return apiFetch<DeliverySettingsDto>("/admin/delivery-settings");
}

export async function updateDeliverySettings(
  patch: Partial<DeliverySettingsDto>,
): Promise<DeliverySettingsDto> {
  return apiFetch<DeliverySettingsDto>("/admin/delivery-settings", {
    method: "PATCH",
    json: patch,
  });
}
